import { Component, signal, ViewChild, ElementRef } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule
} from '@angular/forms';

import { Router, RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatDividerModule } from '@angular/material/divider';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { Token } from '@app/auth/token.model';
import { UserCarrier } from '@app/auth/user.model';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-connections',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatInputModule,
    MatTooltipModule,
    MatIconModule,
    MatProgressSpinner,
    MatStepperModule,
    MatDividerModule,
    FormsModule,
    CommonModule,
    MatCheckboxModule
  ],
  templateUrl: './connection.component.html',
  styleUrl: './connection.component.scss',
})
export class ConnectionComponent {
  readonly isDevelopment = !environment.production;

  @ViewChild('mcInput') mcInput!: ElementRef;
  @ViewChild('stepper') stepper!: MatStepper;

  mcForm: FormGroup;
  tokenForm: FormGroup;

  carrier: any = null;
  carriers: UserCarrier[] = [];

  loading = signal(false);
  connectionExist = signal(false);

  token: Token | null = null;

  lastDeliveryMethod: string | null = null;
  lastIdentifier: string | null = null;

  resendCodeTimerInitial = 30;
  resendCodeInterval: any = null;
  resendCodeTimer = signal(0);

  customEmailPrefix = '';
  saveEmail = false;

  constructor(
    private appService: AppService,
    private authService: AuthService,
    private fb: FormBuilder,
    private router: Router
  ) {
    this.mcForm = this.fb.group({
      mc_number: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
    });

    this.tokenForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      token_uuid: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    this.getMyCarriers();
  }

  getCarrierByMc() {
    const mcNumber = this.mcForm.get('mc_number')?.value;

    if (mcNumber) {
      this.mcForm.patchValue({
        mc_number: String(mcNumber).replace(/\D/g, ''),
      });
    }

    const cleanMcNumber = this.mcForm.get('mc_number')?.value;

    if (!cleanMcNumber) {
      this.appService.showSnackBar('Please enter a MC number', 'error');
      return;
    }

    if (this.mcForm.invalid) {
      this.mcForm.markAllAsTouched();
      return;
    }

    if (this.carrier && String(this.carrier.docket_number) === String(cleanMcNumber)) {
      return;
    }

    this.loading.set(true);

    this.appService.getCarrierByMC(cleanMcNumber).subscribe({
      next: (success) => {
        this.carrier = success.data;
        this.loading.set(false);

        this.connectionExist.set(this.checkExistingConnection());

        if (this.connectionExist()) {
          this.appService.showSnackBar(
            'You already have a connection with this carrier',
            'success'
          );
        }

        this.stepper.next();
      },
      error: (error) => {
        this.appService.handleError(error);
        this.carrier = null;
        this.loading.set(false);
      },
    });
  }

  createToken(identifierType: string, identifier: string, isResend = false, uniqueUserIdentifier = false) {
    if (!this.carrier) {
      this.appService.showSnackBar('Please select a carrier', 'error');
      return;
    }

    if (!identifier) {
      this.appService.showSnackBar('Identifier is empty', 'error');
      return;
    }

    const data = {
      identifier,
      delivery_method: identifierType,
      unique_user_identifier: uniqueUserIdentifier,
    };

    this.loading.set(true);
    this.token = null;

    this.appService.post('create-token', data).subscribe({
      next: (success) => {
        this.token = success.data;

        this.tokenForm.patchValue({
          token_uuid: this.token?.uuid,
          // In development mode, pre-fill the code for testing purposes
          // In production, code will be not returned in response
          code: this.isDevelopment ? success.data.code : '',
        });

        this.lastDeliveryMethod = identifierType;
        this.lastIdentifier = identifier;

        this.appService.showSnackBar('Verification code sent', 'success');
        this.startCodeResendTimer();

        this.loading.set(false);

        if (!isResend) {
          this.stepper.next();
        }
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loading.set(false);
        this.token = null;
      },
    });
  }

  verifyToken() {
    if (!this.token) {
      this.appService.showSnackBar('Verification code expired', 'error');
      return;
    }

    if (this.tokenForm.invalid) {
      this.tokenForm.markAllAsTouched();
      return;
    }

    const payload = {
      code: this.tokenForm.value.code,
      uuid: this.token.uuid,
    };

    this.loading.set(true);

    this.appService.get('verify-token', payload).subscribe({
      next: () => {
        this.createConnection(this.carrier.dot_number, this.token!.uuid);
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loading.set(false);
        this.token = null;
      },
    });
  }

  createConnection(dotNumber: number, tokenUuid: string) {
    const data = {
      dot_number: dotNumber,
      token_uuid: tokenUuid,
      save_email: this.isCustomDomainVerification() ? this.saveEmail : false,
    };

    this.appService.post('user/carrier-connections', data).subscribe({
      next: (success) => {
        this.appService.showSnackBar(success.message, 'success');
        this.setUser();
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loading.set(false);
      },
    });
  } 

  resendToken() {
    if (this.lastDeliveryMethod && this.lastIdentifier) {
      // If the original verification used a custom company domain email,
      // keep enforcing uniqueness when requesting a new verification token.
      this.createToken(
        this.lastDeliveryMethod,
        this.lastIdentifier,
        true,
        this.isCustomDomainVerification()
      );
    }
  }

  startCodeResendTimer() {
    this.clearCodeResendTimer();

    this.resendCodeTimer.set(this.resendCodeTimerInitial);

    this.resendCodeInterval = setInterval(() => {
      if (this.resendCodeTimer() > 0) {
        this.resendCodeTimer.set(this.resendCodeTimer() - 1);
      } else {
        this.clearCodeResendTimer();
      }
    }, 1000);
  }

  clearCodeResendTimer() {
    if (this.resendCodeInterval) {
      clearInterval(this.resendCodeInterval);
      this.resendCodeInterval = null;
    }
  }

  getMyCarriers() {
    this.carriers = this.authService.getUserFromStorage()?.carriers || [];
  }

  checkExistingConnection(): boolean {
    if (!this.carrier) {
      return false;
    }

    return this.carriers.some(
      carrier => carrier.dot_number === this.carrier.dot_number
    );
  }

  setUser() {
    this.authService.getUser().subscribe({
      next: (success) => {
        if (success.data) {
          this.authService.setUserToStorage(success.data[0]);
          this.appService.triggerHeaderRefresh();
          this.router.navigate(['account/connections']);
          this.loading.set(false);
        }
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loading.set(false);
      },
    });
  }

  formatPhone(phone: string | null | undefined): string {
    if (!phone) {
      return '';
    }

    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return phone;
  }

  goBack() {
    this.stepper.previous();
  }

  clear() {
    this.stepper.reset();

    this.mcForm.reset();
    this.tokenForm.reset();

    this.carrier = null;
    this.token = null;
    this.connectionExist.set(false);

    this.lastDeliveryMethod = null;
    this.lastIdentifier = null;

    this.customEmailPrefix = '';
    this.saveEmail = false;

    this.clearCodeResendTimer();
  }

  getCarrierAuthority(carrier: any): boolean {

    if(carrier.allowed_to_operate !=='Y') {
      return false;
    }

    if(carrier.carrier_contract_authority_status == 'A' 
      || carrier.carrier_common_authority_status == 'A') {
      return true;
    }

    return false;
  }

  getBrokerAuthority(carrier: any): boolean {
    if(carrier.allowed_to_operate !== 'Y') {
      return  false;
    }

    if(carrier.broker_authority_status === 'A') {
      return true;
    }

    return false;
  }

  createCustomDomainEmailToken() {
    const prefix = this.customEmailPrefix.trim();

    if (!prefix) {
      this.appService.showSnackBar('Please enter company email name', 'error');
      return;
    }

    if (!/^[a-zA-Z0-9._%+-]+$/.test(prefix)) {
      this.appService.showSnackBar('Invalid email name', 'error');
      return;
    }

    const email = `${prefix}@${this.carrier.email_domain}`.toLowerCase();

    this.createToken('email', email, false, true);
  }

  autoConnect() {
    if (!this.carrier?.dot_number) {
      this.appService.showSnackBar('Please select a company', 'error');
      return;
    }

    const data = {
      dot_number: this.carrier.dot_number,
    };

    this.loading.set(true);

    this.appService.post('user/carrier-connections/auto-connect', data).subscribe({
      next: (success) => {
        this.appService.showSnackBar(success.message, 'success');
        this.setUser();
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loading.set(false);
      },
    });
  }

  isCustomDomainVerification(): boolean {
    if (!this.carrier?.email_domain || !this.lastIdentifier) {
      return false;
    }

    const identifier = this.lastIdentifier.toLowerCase();
    const carrierEmail = this.carrier.email_address?.toLowerCase();

    return (
      identifier.endsWith(`@${this.carrier.email_domain}`) &&
      identifier !== carrierEmail
    );
  }

  ngOnDestroy() {
    this.clearCodeResendTimer();
  }
}